/**
 * authAuthentication
 *
 * Authentication helpers wrapping Firebase Auth flows (email/password, Google)
 * and coordinating with server profile creation/fetch.
 */
import { auth, browserLocalPersistence } from '../../firebase/firebase';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  updateProfile,
  deleteUser,
  setPersistence,
  signInWithPopup,
  signOut
} from 'firebase/auth';
import { createUserProfile, fetchUserProfile } from './authProfile';
import { SERVER_URL } from '../../api/core';
import api from '../../api/core';

// Signs in a user with email and password
export const signIn = async (email, password, setUser, rememberMe = false) => {
  try {
    // CRITICAL FIX: Always set LOCAL persistence for maximum token retention
    await setPersistence(auth, browserLocalPersistence);
    console.log('Auth persistence set to LOCAL for maximum token retention on signin');

    // Sign in with email and password
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Store auth state info for potential redirect recovery
    try {
      sessionStorage.setItem('auth_is_persistent', 'true');
      const authBackup = {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        timestamp: Date.now()
      };
      localStorage.setItem('auth_backup', JSON.stringify(authBackup));
    } catch (storageError) {
      console.warn('Failed to save auth backup:', storageError);
    }

    // OPTIMIZED: Get a fresh token only after sign-in to ensure it's valid
    // This is necessary after authentication to ensure we have a proper token
    const idToken = await userCredential.user.getIdToken(true);
    console.log('Fresh ID token obtained after signin, length:', idToken?.length);

    // Get the user profile
    const profile = await fetchUserProfile(userCredential.user, setUser);
    
    return profile;
  } catch (error) {
    console.error('Error during sign in:', error);
    // Handle auth errors and preserve error codes
    if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential' || error.code === 'auth/invalid-email') {
      const friendlyError = new Error('Invalid email or password');
      friendlyError.code = error.code; // Preserve the original error code
      throw friendlyError;
    } else if (error.code === 'auth/too-many-requests') {
      const friendlyError = new Error('Too many failed attempts. Please try again later.');
      friendlyError.code = error.code; // Preserve the original error code
      throw friendlyError;
    }
    throw error;
  }
};

// Signs up a new user and creates their profile on the server
export const signUp = async (
  email,
  password,
  firstName,
  lastName,
  plan = "basic",
  setNewSignup,
  rememberMe = false,
  extraProfile = null
) => {
  try {
    // Display a message while signing up
    console.log(`Creating new user with email: ${email}, name: ${firstName} ${lastName}, plan: ${plan}`);

    // CRITICAL FIX: Always set LOCAL persistence for maximum token retention
    await setPersistence(auth, browserLocalPersistence);
    console.log('Auth persistence set to LOCAL for maximum token retention on signup');

    // Create user in Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    // Include user UID for debugging
    console.log(`New user created in Firebase Auth with UID: ${userCredential.user.uid}`);
    console.log(`Email: ${userCredential.user.email}`);

    // OPTIMIZED: Get a fresh token only after signup to ensure it's valid for profile creation
    // This is necessary after authentication to ensure we have a proper token for the API call
    const idToken = await userCredential.user.getIdToken(true);
    console.log('Fresh ID token obtained after signup, length:', idToken?.length);

    // Create profile in backend with first name and last name using the correct URL
    const response = await fetch(`${SERVER_URL}/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        firstName,
        lastName,
        plan,
        // Forward optional extended profile fields
        ...(extraProfile ? { 
          role: extraProfile.role || null,
          phone: extraProfile.phone || null,
          hasSeenContactInfoModal: extraProfile.hasSeenContactInfoModal === true
        } : {})
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create user profile');
    }

    const profileResponse = await response.json();

    // Check for the new newSignup flag in the response
    if (profileResponse && profileResponse.newSignup === true) {
      console.log('New signup confirmed by server with timestamp:', profileResponse.authTimestamp);
      
      // Store auth state immediately after signup to prevent issues with redirects
      localStorage.setItem('just_signed_up', 'true');
      localStorage.setItem('auth_timestamp', profileResponse.authTimestamp);
      sessionStorage.setItem('auth_is_persistent', 'true');
      
      // Store user data to help with restoring session if needed
      try {
        const authBackup = {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          timestamp: Date.now(),
          firstName,
          lastName
        };
        localStorage.setItem('auth_backup', JSON.stringify(authBackup));
        console.log('Auth backup saved to localStorage');
      } catch (storageError) {
        console.warn('Failed to save auth backup:', storageError);
      }
    }

    // Set the new signup flag
    if (setNewSignup) {
      setNewSignup(true);
    }

    // Notify user of successful signup
    console.log(`User profile created for ${firstName} ${lastName} with plan ${plan}`);

    // Wait a moment to ensure all Firebase auth operations are complete
    await new Promise(resolve => setTimeout(resolve, 500));

    return profileResponse.user;
  } catch (error) {
    console.error('Error during signup process:', error);
    
    // Handle common signup errors and preserve error codes
    if (error.code === 'auth/email-already-in-use') {
      const friendlyError = new Error('This email is already in use. Please try a different email or sign in instead.');
      friendlyError.code = error.code; // Preserve the original error code
      throw friendlyError;
    } else if (error.code === 'auth/invalid-email') {
      const friendlyError = new Error('Invalid email format. Please check your email address.');
      friendlyError.code = error.code; // Preserve the original error code
      throw friendlyError;
    } else if (error.code === 'auth/weak-password') {
      const friendlyError = new Error('Password is too weak. Please use a stronger password.');
      friendlyError.code = error.code; // Preserve the original error code
      throw friendlyError;
    } else if (error.response) {
      // Handle errors from the API
      const errorMsg = error.response.data.error || 'An error occurred during signup. Please try again.';
      throw new Error(errorMsg);
    }
    throw error;
  }
};

// Completes the new signup flow by fetching the user profile
export const completeNewSignup = async (setNewSignup, setUser) => {
  setNewSignup(false);
};

// Signs up with Google
export const signUpWithGoogle = async (firebaseUser, firstName, lastName, plan = 'basic', setNewSignup, setUser, rememberMe = false) => {
  // Always use local persistence for long-term sessions regardless of "remember me"
  const persistenceType = browserLocalPersistence;
  
  try {
    console.log('Starting Google signup process for user:', firebaseUser?.uid, 'with plan:', plan);
    
    // First set the persistence
    await setPersistence(auth, persistenceType);
    
    if (!firebaseUser) {
      throw new Error('Firebase authentication failed. No user found after Google sign-up.');
    }
    
    try {
      // Create user profile on our server
      console.log('Creating server-side profile for Google user...');
      
      // Explicitly pass the plan parameter to createUserProfile
      const userProfile = await createUserProfile(firebaseUser, firstName, lastName, plan, { hasSeenContactInfoModal: false });
      console.log('Server-side profile created successfully for Google user:', userProfile);
      
      // Set newSignup to true 
      if (setNewSignup) {
        setNewSignup(true);
      }
      
      // Set the user profile with the data returned from createUserProfile
      if (setUser) {
        setUser(userProfile);
      }
      
      // Always store persistence preference as local
      localStorage.setItem('auth_persistence', 'local');
      
      return userProfile;
    } catch (error) {
      // If there's an error creating the profile, clean up the Firebase account
      console.error('Error creating user profile during Google sign-up:', error);
      
      // Add additional error details
      const enhancedError = new Error(error.message || 'Failed to create user profile');
      if (error.status) enhancedError.status = error.status;
      enhancedError.code = error.code || 'auth/profile-creation-failed';
      
      // CRITICAL FIX: Only delete the Firebase account for certain error types
      // Do NOT delete for "already exists" errors because the Google account should be preserved
      const shouldDeleteFirebaseAccount = !(
        error.errorCode === 'EMAIL_ALREADY_EXISTS' || 
        error.errorCode === 'PROFILE_ALREADY_EXISTS' ||
        (error.message && (
          error.message.includes('An account with this email address already exists') ||
          error.message.includes('email is already registered') ||
          error.message.includes('user profile for this account already exists') ||
          error.message.includes('already exists')
        )) ||
        error.code === 'auth/email-already-in-use'
      );
      
      if (shouldDeleteFirebaseAccount) {
        // Clean up the Firebase account only for errors that aren't "already exists"
        try {
          if (auth.currentUser) {
            await deleteUser(auth.currentUser);
            console.log('Firebase account deleted after profile creation error');
          }
        } catch (deleteError) {
          console.error('Error deleting Firebase account:', deleteError);
        }
      } else {
        console.log('Preserving Firebase account because error indicates email/profile already exists');
      }
      
      throw enhancedError;
    }
  } catch (error) {
    console.error('Error signing up with Google:', error);
    throw error;
  }
};

// Signs in with Google using a popup credential
export const signInWithGoogle = async (firebaseUser, firstName, lastName, plan = 'basic', setUser, rememberMe = false, skipProfileCheck = false, setNewSignup = null) => {
  // Ensure the in-progress flag is set (may have been cleared by UI cleanup)
  sessionStorage.setItem('googleSignInInProgress', 'true');

  try {
    // CRITICAL FIX: Always use LOCAL persistence for maximum token retention
    await setPersistence(auth, browserLocalPersistence);
    console.log('Auth persistence set to LOCAL for maximum token retention on Google signin');
    
    // Check if we have a valid user
    if (!firebaseUser) {
      throw new Error('Firebase authentication failed. No user found after Google sign-in.');
    }
    
    // CRITICAL: Store auth state info for potential redirect recovery (like regular signIn does)
    try {
      sessionStorage.setItem('auth_is_persistent', 'true');
      const authBackup = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        timestamp: Date.now(),
        firstName,
        lastName
      };
      localStorage.setItem('auth_backup', JSON.stringify(authBackup));
      console.log('Auth backup saved to localStorage for Google sign-in');
    } catch (storageError) {
      console.warn('Failed to save auth backup for Google sign-in:', storageError);
    }
    
    // OPTIMIZED: Get a fresh token only after Google sign-in to ensure it's valid
    // This is necessary after authentication to ensure we have a proper token for profile checks
    try {
      const idToken = await firebaseUser.getIdToken(true);
      console.log('Fresh ID token obtained for Google sign-in user, length:', idToken?.length);
    } catch (tokenError) {
      console.error('Error getting/refreshing token for Google user:', tokenError);
      throw new Error('Failed to establish secure session. Please try again.');
    }
    
    try {
      // Skip the user existence check if skipProfileCheck is true
      if (skipProfileCheck) {
        console.log('Skipping user existence check as requested');
        // Directly fetch the profile
        try {
          const userProfile = await fetchUserProfile(firebaseUser, setUser, true);
          
          // Always store persistence preference as local
          localStorage.setItem('auth_persistence', 'local');
          
          // Always ensure we properly set the user
          if (setUser && userProfile) {
            setUser(userProfile);
          }
          
          return userProfile || firebaseUser;
        } catch (profileError) {
          console.error('Error fetching profile during Google sign-in with skipProfileCheck:', profileError);
          // When skipProfileCheck is true but profile fetch fails, still return the user
          // as we trust the caller has verified the user exists
          return firebaseUser;
        }
      }
      
      // OPTIMIZED: Combine user existence check with profile fetch to avoid duplicate API calls
      // Instead of calling checkUserExists() and then fetchUserProfile(), just call fetchUserProfile()
      // and handle the 404 case appropriately
      try {
        console.log('Fetching user profile (this also checks if user exists)');
        const userProfile = await fetchUserProfile(firebaseUser, setUser, true);
        
        // Always store persistence preference as local
        localStorage.setItem('auth_persistence', 'local');
        
        return userProfile || firebaseUser;
      } catch (profileError) {
        // If profile fetch fails with 404, that means user doesn't exist in our system
        if (profileError.status === 404 || 
            profileError.code === 'auth/profile-not-found' || 
            (profileError.message && profileError.message.includes('Profile not found'))) {
          console.log('Profile fetch failed with 404 - user needs to be created');
          
          // CRITICAL: Instead of throwing an error, create the user profile
          // This is the new behavior to support auto-creation on Google sign-in
          try {
            console.log('Creating new user profile for Google sign-in user:', firebaseUser.email);
            
            // Create the user profile with the same approach as sign-up
            const newUserProfile = await createUserProfile(firebaseUser, firstName, lastName, plan);
            console.log('Successfully created user profile for Google sign-in:', newUserProfile);
            
            // Set the new signup flag if provided (to trigger pricing page redirect)
            if (setNewSignup) {
              console.log('Setting newSignup to true for Google sign-in created user');
              setNewSignup(true);
            }
            
            // Set flags to indicate this is a new user
            localStorage.setItem('just_signed_up', 'true');
            sessionStorage.setItem('justLoggedIn', 'true');
            sessionStorage.setItem('coming_from_signup', 'true');
            sessionStorage.setItem('needs_auth_refresh', 'true');
            
            // Store auth recovery data
            const authRecoveryData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              provider: 'google.com',
              timestamp: Date.now(),
              firstName,
              lastName
            };
            localStorage.setItem('auth_recovery_data', JSON.stringify(authRecoveryData));
            localStorage.setItem('google_signin_timestamp', Date.now().toString());
            sessionStorage.setItem('auth_method', 'google');
            
            // CRITICAL: Ensure the new user profile has the correct plan level
            // This prevents the PricingPage from showing infinite loading
            if (newUserProfile && !newUserProfile.planLevel) {
              newUserProfile.planLevel = 'basic';
            }
            
            // Set the user profile
            if (setUser && newUserProfile) {
              setUser(newUserProfile);
            }
            
            // Always store persistence preference as local
            localStorage.setItem('auth_persistence', 'local');
            
            return newUserProfile;
          } catch (createError) {
            console.error('Error creating user profile during Google sign-in:', createError);
            
            // Clear the Google sign-in flag on error
            sessionStorage.removeItem('googleSignInInProgress');
            
            // If user profile creation fails (e.g., email already exists in another form),
            // check if this might be an orphaned user that needs repair instead of cleanup
            if (createError.message && createError.message.includes('already exists')) {
              console.log('Profile creation failed due to existing user, this might be an orphaned user');
              
              // Import the orphaned user utilities
              try {
                const { detectAndRepairOrphanedUser } = await import('../../utils/admin/orphanedUserUtils');
                
                console.log('Attempting to repair potentially orphaned user during Google sign-in...');
                const repairedUser = await detectAndRepairOrphanedUser(
                  firebaseUser,
                  setUser,
                  {
                    defaultFirstName: firstName,
                    defaultLastName: lastName,
                    onRepairStart: () => console.log('Starting orphaned user repair during Google sign-in...'),
                    onRepairComplete: (data) => console.log('Orphaned user repair completed during Google sign-in:', data),
                    onRepairError: (error) => console.error('Orphaned user repair failed during Google sign-in:', error)
                  }
                );
                
                if (repairedUser) {
                  console.log('Successfully repaired orphaned user during Google sign-in');
                  
                  // Set flags for new user flow
                  localStorage.setItem('just_signed_up', 'true');
                  sessionStorage.setItem('justLoggedIn', 'true');
                  sessionStorage.setItem('coming_from_signup', 'true');
                  
                  // Store auth recovery data
                  const authRecoveryData = {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email,
                    displayName: firebaseUser.displayName,
                    photoURL: firebaseUser.photoURL,
                    provider: 'google.com',
                    timestamp: Date.now(),
                    firstName,
                    lastName,
                    repaired: true
                  };
                  localStorage.setItem('auth_recovery_data', JSON.stringify(authRecoveryData));
                  localStorage.setItem('google_signin_timestamp', Date.now().toString());
                  sessionStorage.setItem('auth_method', 'google');
                  localStorage.setItem('auth_persistence', 'local');
                  
                  return repairedUser;
                }
              } catch (repairError) {
                console.error('Error attempting to repair orphaned user during Google sign-in:', repairError);
                // Fall through to original cleanup logic
              }
            }
            
            // Original cleanup logic for non-repairable errors
            try {
              await auth.signOut();
              console.log('Signed out after profile creation failure');
            } catch (signOutError) {
              console.error('Error signing out after profile creation failure:', signOutError);
            }
            
            // Throw the error from profile creation
            throw createError;
          }
        } else {
          // For other errors, just propagate
          throw profileError;
        }
      }
    } catch (error) {
      console.error('Error during Google sign-in profile check:', error);
      throw error;
    }
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw error;
  }
};

// Helper function to check if a user exists without fetching their entire profile
const checkUserExists = async (user) => {
  try {
    console.log('Checking if user exists:', user?.uid);
    
    if (!user) {
      const error = new Error('No user account found for this Google login');
      error.code = 'auth/user-not-found';
      throw error;
    }
    
    // Get the user's email
    const email = user.email;
    
    if (!email) {
      const error = new Error('No email found for this Google account');
      error.code = 'auth/invalid-email';
      throw error;
    }
    
    // OPTIMIZED: Get a token first without forcing refresh, only force if needed
    console.log('Getting ID token for user existence check:', user.uid);
    let idToken;
    try {
      // Try current token first
      idToken = await user.getIdToken(false);
      console.log('Successfully got current ID token of length:', idToken?.length);
    } catch (tokenError) {
      console.log('Current token failed, forcing refresh for user existence check');
      // If current token fails, then force refresh
      idToken = await user.getIdToken(true);
      console.log('Successfully got fresh ID token of length:', idToken?.length);
    }
    
    // CRITICAL FIX: Since the check-email-exists endpoint seems to be returning 404,
    // let's directly check if the user has a profile in our system
    try {
      console.log('Checking if user profile exists in our system');
      const profileResponse = await fetch(`${SERVER_URL}/auth/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Profile check API response status:', profileResponse.status);
      
      if (profileResponse.ok) {
        // User exists in our system and can sign in
        console.log('User profile exists - sign in allowed');
        return true;
      } else if (profileResponse.status === 404) {
        // User doesn't exist in our system - they need to sign up first
        console.log('User profile does not exist (404) - user needs to sign up first');
        const error = new Error('No account exists with this Google account. Please sign up first.');
        error.code = 'auth/user-not-found';
        error.status = 404;
        throw error;
      } else if (profileResponse.status === 401) {
        // Unauthorized - token might be invalid
        console.error('Unauthorized response when checking profile - token may be invalid');
        const error = new Error('Authentication failed. Please try signing in again.');
        error.code = 'auth/invalid-token';
        error.status = 401;
        throw error;
      } else {
        // Other server error
        console.error('Server error while checking user profile:', profileResponse.status);
        const error = new Error('Server error while checking account. Please try again.');
        error.code = 'auth/server-error';
        error.status = profileResponse.status;
        throw error;
      }
    } catch (fetchError) {
      // If this is already a formatted error with a code, just pass it through
      if (fetchError.code) {
        console.error('Error with code while checking user profile:', fetchError.code);
        throw fetchError;
      }
      
      // For network errors or other fetch issues
      console.error('Network error while checking user profile:', fetchError.message);
      const error = new Error('Network error while verifying account: ' + fetchError.message);
      error.code = 'auth/network-error';
      throw error;
    }
  } catch (error) {
    console.error('Error checking if user exists:', error);
    
    // Make sure any error has proper status and code for consistent handling
    if (error.status === 404 || (error.response && error.response.status === 404)) {
      if (!error.code) error.code = 'auth/user-not-found';
    }
    
    // Store error in localStorage for potential recovery in the UI
    if (error.message) {
      localStorage.setItem('refrral_auth_error', error.message);
    }
    
    throw error;
  }
};

